/**
 * EventStream — Unified event stream with search & filter
 *
 * Replaces the old 3-panel Events tab with a single dense, searchable,
 * filterable stream of ALL telemetry events for a sprint.
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown, ChevronRight, GitCommit, FileText, AlertTriangle, RefreshCw, Filter, EyeOff, Eye } from 'lucide-react';
import { useTelemetryEvents, type TelemetryEvent, type TelemetryEventFilters } from '../../api/telemetry';

/* ── Severity styling ─────────────────────────────────────────── */

const SEV_STYLE: Record<string, { pill: string; dot: string }> = {
  critical: { pill: 'text-red-700 bg-red-100', dot: 'bg-red-600' },
  error:    { pill: 'text-red-600 bg-red-50', dot: 'bg-red-500' },
  warning:  { pill: 'text-amber-600 bg-amber-50', dot: 'bg-amber-500' },
  info:     { pill: 'text-neutral-500 bg-neutral-100', dot: 'bg-neutral-400' },
  success:  { pill: 'text-accent-600 bg-accent-50', dot: 'bg-accent-500' },
  debug:    { pill: 'text-neutral-400 bg-neutral-50', dot: 'bg-neutral-300' },
};

/** Category groupings for event types — used for "Category" filter row */
const CATEGORY_MAP: Record<string, string> = {
  // Lifecycle
  workflow_started: 'lifecycle', workflow_completed: 'lifecycle',
  code_execution_started: 'lifecycle', code_execution_completed: 'lifecycle',
  code_execution_cancelled: 'lifecycle',
  epic_execution_started: 'lifecycle', epic_execution_completed: 'lifecycle', epic_execution_failed: 'lifecycle',
  epic_queued: 'lifecycle', epic_resumed_from_checkpoint: 'lifecycle',
  epic_skipped_resume: 'lifecycle',
  story_execution_started: 'lifecycle', story_execution_completed: 'lifecycle',
  story_retry_budget: 'lifecycle', story_skipped_resume: 'lifecycle',
  story_denylisted: 'lifecycle',
  preflight_config_resolved: 'lifecycle',
  key_cycle_triggered: 'lifecycle',
  sprint_inter_epic_cooldown: 'lifecycle',
  enrichment_loaded: 'lifecycle',
  wave_plan_computed: 'lifecycle',
  batch_execution_started: 'lifecycle', batch_execution_completed: 'lifecycle',
  batch_recovery_completed: 'lifecycle',
  test_issue_execution_started: 'lifecycle', test_issue_execution_completed: 'lifecycle',

  // Sprite
  sprite_spawned: 'sprite', sprite_destroyed: 'sprite', sprite_heartbeat: 'sprite',
  sprite_phase: 'sprite', sprite_phase_changed: 'sprite',
  sprite_infra_failure_detected: 'sprite',
  sprite_file_created: 'sprite', sprite_file_modified: 'sprite', sprite_file_deleted: 'sprite',
  agent_started: 'sprite', agent_completed: 'sprite', agent_blocked: 'sprite',
  sprite_spawn_failed: 'sprite',

  // Claude
  claude_session_init: 'claude', claude_session_result: 'claude',
  claude_tool_use: 'claude', claude_tool_result: 'claude', claude_text_output: 'claude',

  // Build
  commit_made: 'build', test_results: 'build',
  build_started: 'build', build_completed: 'build',
  test_started: 'build',
  repair_started: 'build', repair_completed: 'build',
  config_self_healed: 'build',
  architecture_context_committed: 'build',
  test_issues_deferred: 'build',
  post_build_test_started: 'build', post_build_test_completed: 'build', post_build_test_failed: 'build',
  preview_available: 'build',
  smoke_tests_completed: 'build', smoke_tests_failed: 'build',
  visual_qa_started: 'build', visual_qa_completed: 'build', visual_qa_failed: 'build',
  test_wave_started: 'build', test_wave_completed: 'build',
  sprite_lint_started: 'build', sprite_lint_passed: 'build', sprite_lint_failed: 'build',
  sprite_typecheck_started: 'build', sprite_typecheck_passed: 'build', sprite_typecheck_failed: 'build',
  epic_quality_gate: 'build', epic_quality_gate_evaluated: 'build',
  epic_build_verification_started: 'build', epic_build_verification_failed: 'build',
  auto_bdd_verification_started: 'build', auto_bdd_verification_completed: 'build',
  auto_bdd_verification_failed: 'build', auto_bdd_verification_passed: 'build',
  sprint_pr_created: 'build',

  // Error
  epic_circuit_breaker_open: 'error', epic_circuit_breaker_close: 'error',
  epic_circuit_breaker_abort: 'error', epic_circuit_breaker_pause: 'error',
  epic_rapid_failure_abort: 'error',
  epic_critical_abort: 'error',
  sprite_infra_failure_abort: 'error',
  sprint_systemic_abort: 'error',
  sprint_key_exhaustion_partial: 'error',
  error_detected: 'error',
  evidence_recording_failed: 'error',
  pr_creation_failed: 'error',
  workflow_failed: 'error',
};
const CATEGORY_LABELS: Record<string, string> = {
  lifecycle: 'Lifecycle', sprite: 'Sprite', claude: 'Claude', build: 'Build', error: 'Error',
};

/** Event types considered "noise" — hidden by default to reduce clutter */
const NOISE_EVENT_TYPES = new Set(['sprite_heartbeat', 'query_executed', 'supervisor_observation']);

/* ── Detail renderers ─────────────────────────────────────────── */

function ExpandedPayload({ evt }: { evt: TelemetryEvent }) {
  const p = evt.payload || {};

  if (evt.eventType === 'story_execution_completed') {
    return <StoryCompletedDetail payload={p} errorMessage={evt.errorMessage} durationMs={evt.durationMs} />;
  }
  if (evt.eventType === 'commit_made') {
    return <CommitDetail payload={p} />;
  }
  if (evt.eventType.endsWith('_failed') || evt.severity === 'error' || evt.severity === 'critical') {
    return <ErrorDetail payload={p} errorMessage={evt.errorMessage} errorStack={evt.errorStack} />;
  }
  return <GenericPayload payload={p} errorMessage={evt.errorMessage} />;
}

function StoryCompletedDetail({ payload, errorMessage, durationMs }: { payload: Record<string, unknown>; errorMessage: string | null; durationMs: number | null }) {
  const status = payload.status as string | undefined;
  const files = (payload.files as string[]) || [];
  const filesChangedCount = (payload.filesChanged as number) || 0;
  const commits = (payload.commits as Array<{ sha?: string; message?: string; files?: string[] }>) || [];
  const testResults = payload.testResults as { passed?: number; failed?: number; skipped?: number } | null;
  const outputExcerpt = payload.outputExcerpt as string | undefined;
  const effectiveDuration = (payload.duration as number) || durationMs;
  const error = (payload.errorMessage as string) || errorMessage;
  const isFailed = status === 'failure' || status === 'failed' || status === 'error';

  return (
    <div className="bg-neutral-50 border-t border-neutral-100 px-6 py-3 space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${isFailed ? 'text-red-600' : 'text-accent-600'}`}>
          {status || 'unknown'}
        </span>
        {effectiveDuration != null && (
          <span className="text-[10px] font-mono text-neutral-500">
            {effectiveDuration >= 60_000 ? `${Math.round(effectiveDuration / 60_000)}m` : `${Math.round(effectiveDuration / 1000)}s`}
          </span>
        )}
        {(files.length > 0 || filesChangedCount > 0) && (
          <span className="flex items-center gap-1 text-[10px] text-neutral-500">
            <FileText size={10} /> {files.length || filesChangedCount} files
          </span>
        )}
        {commits.length > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-neutral-500">
            <GitCommit size={10} /> {commits.length} commits
          </span>
        )}
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-2">
          <p className="text-[10px] font-mono text-red-700 whitespace-pre-wrap break-words">{error}</p>
        </div>
      )}
      {commits.length > 0 && (
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-1">Commits</p>
          <div className="space-y-1">
            {commits.map((c, i) => (
              <div key={c.sha || i} className="flex items-center gap-2">
                {c.sha && <span className="text-[9px] font-mono text-coral-600 bg-coral-50 px-1 py-0.5 rounded">{String(c.sha).slice(0, 7)}</span>}
                <span className="text-[10px] text-neutral-600 truncate">{c.message || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {testResults && (testResults.passed != null || testResults.failed != null) && (
        <div className="flex items-center gap-3">
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Tests</p>
          {testResults.passed != null && <span className="text-[10px] font-mono text-accent-600">{testResults.passed} passed</span>}
          {testResults.failed != null && testResults.failed > 0 && <span className="text-[10px] font-mono text-red-600">{testResults.failed} failed</span>}
          {testResults.skipped != null && testResults.skipped > 0 && <span className="text-[10px] font-mono text-amber-600">{testResults.skipped} skipped</span>}
        </div>
      )}
      {files.length > 0 && (
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-1">Files</p>
          <div className="max-h-[120px] overflow-y-auto space-y-0.5">
            {files.map((f) => <p key={f} className="text-[9px] font-mono text-neutral-500 truncate">{f}</p>)}
          </div>
        </div>
      )}
      {outputExcerpt && (
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-1">Output</p>
          <div className="bg-neutral-900 rounded p-2 max-h-[100px] overflow-y-auto">
            <pre className="text-[9px] font-mono text-neutral-300 whitespace-pre-wrap break-words">{outputExcerpt.slice(-1000)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function CommitDetail({ payload }: { payload: Record<string, unknown> }) {
  const sha = (payload.sha as string) || (payload.commitSha as string) || '';
  const message = (payload.message as string) || (payload.commitMessage as string) || '';
  const files = (payload.files as string[]) || (payload.changedFiles as string[]) || [];

  return (
    <div className="bg-neutral-50 border-t border-neutral-100 px-6 py-3 space-y-2">
      <div className="flex items-center gap-2">
        <GitCommit size={12} className="text-coral-500" />
        {sha && <span className="text-[10px] font-mono text-coral-600 bg-coral-50 px-1.5 py-0.5 rounded">{sha.slice(0, 7)}</span>}
        <span className="text-[11px] text-neutral-700">{message}</span>
      </div>
      {files.length > 0 && (
        <div className="space-y-0.5 pl-5">
          {files.map((f) => <p key={f} className="text-[9px] font-mono text-neutral-400 truncate">{f}</p>)}
        </div>
      )}
    </div>
  );
}

function ErrorDetail({ payload, errorMessage, errorStack }: { payload: Record<string, unknown>; errorMessage: string | null; errorStack: string | null }) {
  const msg = (payload.errorMessage as string) || (payload.error as string) || errorMessage || 'Unknown error';
  return (
    <div className="bg-red-50/50 border-t border-red-100 px-6 py-3 space-y-2">
      <p className="text-[11px] font-mono text-red-700 whitespace-pre-wrap break-words">{msg}</p>
      {errorStack && (
        <details className="text-[9px]">
          <summary className="text-red-500 cursor-pointer hover:text-red-600">Stack trace</summary>
          <pre className="mt-1 text-[8px] font-mono text-red-400 whitespace-pre-wrap break-words max-h-[120px] overflow-y-auto">{errorStack}</pre>
        </details>
      )}
      <GenericPayload payload={payload} errorMessage={null} excludeKeys={['errorMessage', 'error']} />
    </div>
  );
}

function GenericPayload({ payload, errorMessage, excludeKeys }: { payload: Record<string, unknown>; errorMessage: string | null; excludeKeys?: string[] }) {
  const entries = Object.entries(payload).filter(([k]) => !(excludeKeys || []).includes(k));
  if (entries.length === 0 && !errorMessage) return null;
  return (
    <div className="bg-neutral-50 border-t border-neutral-100 px-6 py-3">
      {errorMessage && <p className="text-[10px] font-mono text-red-600 mb-2">{errorMessage}</p>}
      {entries.length > 0 && (
        <div className="space-y-1">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-start gap-2">
              <span className="text-[9px] font-mono text-neutral-400 shrink-0 min-w-[80px]">{k}</span>
              <span className="text-[10px] font-mono text-neutral-600 break-all">
                {typeof v === 'string' ? v : JSON.stringify(v, null, 0)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Filter pill component ────────────────────────────────────── */

function FilterPill({ label, count, active, onClick }: { label: string; count?: number; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors border
        ${active
          ? 'bg-neutral-800 text-white border-neutral-800'
          : 'bg-white text-neutral-500 border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300'
        }`}
    >
      {label}
      {count != null && <span className={`font-mono text-[9px] ${active ? 'text-neutral-300' : 'text-neutral-400'}`}>{count}</span>}
    </button>
  );
}

/* ── Main Component ───────────────────────────────────────────── */

interface EventStreamProps {
  batchId?: string;
  sprintId?: string;
  workflowId?: string;
}

export default function EventStream({ batchId, sprintId, workflowId }: EventStreamProps) {
  const [searchText, setSearchText] = useState('');
  const [severityFilter, setSeverityFilter] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [issueFilter, setIssueFilter] = useState<Set<string>>(new Set());
  const [epicFilter, setEpicFilter] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [hideNoise, setHideNoise] = useState(true);
  const [limit, setLimit] = useState(1000);
  const tableRef = useRef<HTMLDivElement>(null);

  // Build server-side filter params
  // NOTE: Only use batchId for scoping — sprintId queries payload->>'sprintId'
  // which isn't populated on most events, causing AND to return 0 results.
  const serverFilters = useMemo<TelemetryEventFilters>(() => {
    const f: TelemetryEventFilters = { limit };
    if (batchId) f.batchId = batchId;
    if (workflowId) f.workflowId = workflowId;
    // No event type exclusions — show ALL telemetry including heartbeats
    // Single server-side filters (API accepts one value per field)
    if (severityFilter.size === 1) f.severity = [...severityFilter][0];
    if (typeFilter.size === 1) f.eventType = [...typeFilter][0];
    if (issueFilter.size === 1) f.issueId = [...issueFilter][0];
    if (epicFilter.size === 1) f.epicId = [...epicFilter][0];
    return f;
  }, [batchId, workflowId, severityFilter, typeFilter, issueFilter, epicFilter, limit]);

  const { data, isLoading, dataUpdatedAt } = useTelemetryEvents(serverFilters);
  const allEvents = data?.events || [];
  const totalCount = data?.count ?? 0;

  // Track new events for fade-in highlight
  const prevEventIdsRef = useRef<Set<string>>(new Set());
  const [newEventIds, setNewEventIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!allEvents.length) return;
    const currentIds = new Set(allEvents.map(e => e.id));
    const prev = prevEventIdsRef.current;
    prevEventIdsRef.current = currentIds;

    if (prev.size === 0) return; // first load — don't highlight everything

    const fresh = new Set<string>();
    for (const id of currentIds) {
      if (!prev.has(id)) fresh.add(id);
    }
    if (fresh.size > 0) {
      setNewEventIds(fresh);
      const timer = setTimeout(() => setNewEventIds(new Set()), 2200);
      return () => clearTimeout(timer);
    }
  }, [allEvents]);

  // Extract facets from full data for filter pills
  const facets = useMemo(() => {
    const types = new Map<string, number>();
    const severities = new Map<string, number>();
    const categories = new Map<string, number>();
    const issues = new Map<string, number>();
    const epics = new Map<string, number>();
    for (const evt of allEvents) {
      types.set(evt.eventType, (types.get(evt.eventType) || 0) + 1);
      severities.set(evt.severity, (severities.get(evt.severity) || 0) + 1);
      const cat = CATEGORY_MAP[evt.eventType] || 'other';
      categories.set(cat, (categories.get(cat) || 0) + 1);
      if (evt.issueId) issues.set(evt.issueId, (issues.get(evt.issueId) || 0) + 1);
      if (evt.epicId) epics.set(evt.epicId, (epics.get(evt.epicId) || 0) + 1);
    }
    return {
      types: [...types.entries()].sort((a, b) => b[1] - a[1]),
      severities: [...severities.entries()].sort((a, b) => {
        const order = ['critical', 'error', 'warning', 'info', 'success', 'debug'];
        return order.indexOf(a[0]) - order.indexOf(b[0]);
      }),
      categories: [...categories.entries()].sort((a, b) => b[1] - a[1]),
      issues: [...issues.entries()].sort((a, b) => b[1] - a[1]),
      epics: [...epics.entries()].sort((a, b) => b[1] - a[1]),
    };
  }, [allEvents]);

  // Client-side filtering (text search + multi-select filters the API can't handle)
  const filteredEvents = useMemo(() => {
    let events = allEvents;

    // Hide noisy event types by default (heartbeats, query_executed, supervisor_observation)
    if (hideNoise) {
      events = events.filter(e => !NOISE_EVENT_TYPES.has(e.eventType));
    }

    // Category filter
    if (categoryFilter.size > 0) {
      events = events.filter(e => {
        const cat = CATEGORY_MAP[e.eventType] || 'other';
        return categoryFilter.has(cat);
      });
    }

    // Multi-select client-side filters (when server couldn't filter because >1 selected)
    if (severityFilter.size > 1) {
      events = events.filter(e => severityFilter.has(e.severity));
    }
    if (typeFilter.size > 1) {
      events = events.filter(e => typeFilter.has(e.eventType));
    }
    if (issueFilter.size > 1) {
      events = events.filter(e => e.issueId && issueFilter.has(e.issueId));
    }
    if (epicFilter.size > 1) {
      events = events.filter(e => e.epicId && epicFilter.has(e.epicId));
    }

    // Text search
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      events = events.filter(e => {
        if (e.eventType.toLowerCase().includes(q)) return true;
        if (e.issueId?.toLowerCase().includes(q)) return true;
        if (e.epicId?.toLowerCase().includes(q)) return true;
        if (e.errorMessage?.toLowerCase().includes(q)) return true;
        if (e.agentId?.toLowerCase().includes(q)) return true;
        if (e.activityName?.toLowerCase().includes(q)) return true;
        if (e.severity.toLowerCase().includes(q)) return true;
        if (e.source?.toLowerCase().includes(q)) return true;
        // Search payload values
        if (e.payload) {
          for (const v of Object.values(e.payload)) {
            const s = typeof v === 'string' ? v : JSON.stringify(v);
            if (s.toLowerCase().includes(q)) return true;
          }
        }
        return false;
      });
    }

    return events;
  }, [allEvents, hideNoise, categoryFilter, severityFilter, typeFilter, issueFilter, epicFilter, searchText]);

  // Toggle helpers
  const toggleSet = useCallback((set: Set<string>, val: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    setter(next);
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchText('');
    setCategoryFilter(new Set());
    setSeverityFilter(new Set());
    setTypeFilter(new Set());
    setIssueFilter(new Set());
    setEpicFilter(new Set());
  }, []);

  const hasActiveFilters = searchText || categoryFilter.size > 0 || severityFilter.size > 0 || typeFilter.size > 0 || issueFilter.size > 0 || epicFilter.size > 0;

  // Auto-scroll on new events (if user is near bottom)
  const isNearBottom = useRef(true);
  useEffect(() => {
    if (isNearBottom.current && tableRef.current) {
      tableRef.current.scrollTop = 0; // newest first, so scroll to top
    }
  }, [dataUpdatedAt]);

  if (!batchId && !sprintId && !workflowId) {
    return (
      <div className="flex items-center justify-center h-48 text-neutral-400 text-sm">
        No sprint selected — start a build to see events.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <style>{`
        @keyframes eventHighlight {
          0% { background-color: rgba(99, 102, 241, 0.08); }
          100% { background-color: transparent; }
        }
      `}</style>
      {/* ── Search + filter bar ── */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm">
        {/* Top row: search + stats */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-neutral-100">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Search size={14} className="text-neutral-400 shrink-0" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search events by type, issue, error, payload..."
              className="flex-1 text-[12px] text-neutral-700 placeholder-neutral-400 outline-none bg-transparent"
            />
            {searchText && (
              <button onClick={() => setSearchText('')} className="text-neutral-400 hover:text-neutral-600">
                <X size={12} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {/* Severity counts */}
            <div className="flex items-center gap-1.5">
              {facets.severities.map(([sev, count]) => {
                const style = SEV_STYLE[sev] || SEV_STYLE.info;
                return (
                  <span key={sev} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${style.pill}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                    {count}
                  </span>
                );
              })}
            </div>

            <span className="text-[10px] font-mono text-neutral-400">
              {filteredEvents.length}{filteredEvents.length !== totalCount ? ` / ${totalCount}` : ''} events
            </span>

            <button
              onClick={() => setHideNoise(h => !h)}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors border
                ${hideNoise
                  ? 'bg-neutral-100 text-neutral-500 border-neutral-200 hover:bg-neutral-50'
                  : 'bg-neutral-800 text-white border-neutral-800'
                }`}
              title={hideNoise ? 'Show heartbeats & noise events' : 'Hide heartbeats & noise events'}
            >
              {hideNoise ? <EyeOff size={11} /> : <Eye size={11} />}
              {hideNoise ? 'Noise hidden' : 'Showing all'}
            </button>

            <button
              onClick={() => setShowFilters(f => !f)}
              className={`p-1 rounded transition-colors ${showFilters ? 'bg-neutral-100 text-neutral-600' : 'text-neutral-400 hover:bg-neutral-50'}`}
              title="Toggle filters"
            >
              <Filter size={13} />
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-[10px] text-coral-500 hover:text-coral-600 font-semibold"
              >
                Clear all
              </button>
            )}

            {isLoading && <RefreshCw size={12} className="text-neutral-400 animate-spin" />}
          </div>
        </div>

        {/* Filter rows */}
        {showFilters && (
          <div className="px-4 py-2 space-y-2 border-b border-neutral-100 bg-neutral-50/50">
            {/* Category */}
            {facets.categories.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 w-14 shrink-0">Category</span>
                {facets.categories.map(([cat, count]) => (
                  <FilterPill
                    key={cat}
                    label={CATEGORY_LABELS[cat] || cat}
                    count={count}
                    active={categoryFilter.has(cat)}
                    onClick={() => toggleSet(categoryFilter, cat, setCategoryFilter)}
                  />
                ))}
              </div>
            )}

            {/* Severity */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 w-14 shrink-0">Severity</span>
              {facets.severities.map(([sev, count]) => (
                <FilterPill
                  key={sev}
                  label={sev}
                  count={count}
                  active={severityFilter.has(sev)}
                  onClick={() => toggleSet(severityFilter, sev, setSeverityFilter)}
                />
              ))}
            </div>

            {/* Event types */}
            {facets.types.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 w-14 shrink-0">Type</span>
                {facets.types.map(([type, count]) => (
                  <FilterPill
                    key={type}
                    label={type}
                    count={count}
                    active={typeFilter.has(type)}
                    onClick={() => toggleSet(typeFilter, type, setTypeFilter)}
                  />
                ))}
              </div>
            )}

            {/* Issues */}
            {facets.issues.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 w-14 shrink-0">Issue</span>
                {facets.issues.map(([id, count]) => (
                  <FilterPill
                    key={id}
                    label={id}
                    count={count}
                    active={issueFilter.has(id)}
                    onClick={() => toggleSet(issueFilter, id, setIssueFilter)}
                  />
                ))}
              </div>
            )}

            {/* Epics */}
            {facets.epics.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 w-14 shrink-0">Epic</span>
                {facets.epics.map(([id, count]) => (
                  <FilterPill
                    key={id}
                    label={id}
                    count={count}
                    active={epicFilter.has(id)}
                    onClick={() => toggleSet(epicFilter, id, setEpicFilter)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Event table ── */}
      <div ref={tableRef} className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
            {isLoading ? (
              <>
                <RefreshCw size={20} className="animate-spin mb-2" />
                <p className="text-sm">Loading events...</p>
              </>
            ) : hasActiveFilters ? (
              <>
                <Search size={20} className="mb-2" />
                <p className="text-sm">No events match your filters</p>
                <button onClick={clearAllFilters} className="mt-2 text-[11px] text-coral-500 hover:text-coral-600 font-semibold">Clear filters</button>
              </>
            ) : (
              <>
                <AlertTriangle size={20} className="mb-2" />
                <p className="text-sm">No events yet</p>
                <p className="text-[11px] mt-1">Events will appear as the sprint executes.</p>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-neutral-200">
                  <th className="text-left py-2 px-2 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider w-5"></th>
                  <th className="text-left py-2 px-2 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider whitespace-nowrap">Time</th>
                  <th className="text-left py-2 px-2 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider">Category</th>
                  <th className="text-left py-2 px-2 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider">Event</th>
                  <th className="text-left py-2 px-2 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider">Issue</th>
                  <th className="text-left py-2 px-2 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider w-16">Severity</th>
                  <th className="text-left py-2 px-2 font-semibold text-neutral-500 uppercase text-[9px] tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((evt) => {
                  const sevStyle = SEV_STYLE[evt.severity] || SEV_STYLE.info;
                  const isExpanded = expandedId === evt.id;
                  const hasPayload = !!(evt.payload && Object.keys(evt.payload).length > 0) || !!evt.errorMessage;
                  const payloadSummary = evt.payload
                    ? Object.entries(evt.payload).slice(0, 4).map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`).join(', ')
                    : '';

                  return (
                    <EventRow
                      key={evt.id}
                      evt={evt}
                      sevStyle={sevStyle}
                      payloadSummary={payloadSummary}
                      isExpanded={isExpanded}
                      hasPayload={hasPayload}
                      searchText={searchText}
                      isNew={newEventIds.has(evt.id)}
                      onToggle={() => setExpandedId(isExpanded ? null : evt.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Load more */}
        {filteredEvents.length > 0 && totalCount > limit && (
          <div className="border-t border-neutral-100 px-4 py-2 flex items-center justify-between bg-neutral-50/50">
            <span className="text-[10px] text-neutral-400">
              Showing {filteredEvents.length} of {totalCount} events
            </span>
            <button
              onClick={() => setLimit(l => l + 500)}
              className="text-[10px] font-semibold text-coral-500 hover:text-coral-600 transition-colors"
            >
              Load 500 more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Event Row ────────────────────────────────────────────────── */

const CAT_STYLE: Record<string, string> = {
  lifecycle: 'text-neutral-500 bg-neutral-100',
  sprite:    'text-coral-600 bg-coral-50',
  claude:    'text-blue-600 bg-blue-50',
  build:     'text-accent-600 bg-accent-50',
  error:     'text-red-600 bg-red-50',
  other:     'text-neutral-400 bg-neutral-50',
};

function EventRow({ evt, sevStyle, payloadSummary, isExpanded, hasPayload, searchText, isNew, onToggle }: {
  evt: TelemetryEvent;
  sevStyle: { pill: string; dot: string };
  payloadSummary: string;
  isExpanded: boolean;
  hasPayload: boolean;
  searchText: string;
  isNew: boolean;
  onToggle: () => void;
}) {
  const detail = payloadSummary || evt.errorMessage || '\u2014';
  const cat = CATEGORY_MAP[evt.eventType] || 'other';

  return (
    <>
      <tr
        className={`border-b border-neutral-50 transition-colors ${hasPayload ? 'cursor-pointer hover:bg-neutral-50/50' : ''} ${isExpanded ? 'bg-neutral-50/70' : ''}`}
        style={isNew ? { animation: 'eventHighlight 2s ease-out' } : undefined}
        onClick={hasPayload ? onToggle : undefined}
      >
        <td className="py-1.5 px-2 text-neutral-400">
          {hasPayload && (isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
        </td>
        <td className="py-1.5 px-2 font-mono text-neutral-400 whitespace-nowrap text-[10px]">
          {new Date(evt.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
        </td>
        <td className="py-1.5 px-2">
          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${CAT_STYLE[cat]}`}>
            {CATEGORY_LABELS[cat] || cat}
          </span>
        </td>
        <td className="py-1.5 px-2 font-mono font-semibold text-neutral-700 whitespace-nowrap">
          <Highlight text={evt.eventType} query={searchText} />
        </td>
        <td className="py-1.5 px-2 font-mono text-neutral-500 whitespace-nowrap">
          {evt.issueId ? <Highlight text={evt.issueId} query={searchText} /> : '\u2014'}
        </td>
        <td className="py-1.5 px-2">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${sevStyle.pill}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${sevStyle.dot}`} />
            {evt.severity}
          </span>
        </td>
        <td className="py-1.5 px-2 text-neutral-400 truncate max-w-[400px] text-[10px]">
          <Highlight text={detail} query={searchText} />
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="p-0">
            <ExpandedPayload evt={evt} />
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Search highlight ─────────────────────────────────────────── */

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query.trim() || !text) return <>{text}</>;
  const q = query.toLowerCase();
  const idx = text.toLowerCase().indexOf(q);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-amber-200/60 rounded-sm px-0.5">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}
